// Mapa de prospecção (Comercial › Painel › aba "Mapa de prospecção"): regras PURAS da tela.
// Nada aqui importa antd, React nem axios (o jest do CRA não carrega antd). A prioridade
// (pontos, nível, motivos) vem pronta do servidor; a tela só filtra, ordena, formata e mostra.
// Contrato das ações: ver ./api.ts e docs/mapa-prospeccao.md.

// ── Tipos do contrato ────────────────────────────────────────────────────────
export type SituacaoMancha = 'dentro' | 'ate_200m' | 'ate_1km' | 'mais_1km' | 'sem_local_fixo' | 'sem_coordenada';
export type NivelPrioridade = 'alta' | 'media' | 'baixa';
export type StatusProspecto = 'novo' | 'contatado' | 'reuniao' | 'proposta' | 'descartado' | 'cliente';

export type Bbox = [number, number, number, number];

export interface Prioridade {
  pontos: number;
  nivel: NivelPrioridade;
  motivos: string[];
}

/** Item da lista (pm_obter): só quantos trechos existem. */
export interface IndicioResumo { codigo: string; nome: string; n: number }
/** Item completo (pm_licenca_texto): até 3 trechos de até 220 letras. */
export interface IndicioCompleto { codigo: string; nome: string; trechos: string[] }

/** Números que a ficha da FEPAM traz; cada um só existe se o sistema achou. */
export interface LicencaNumeros {
  empregados?: number | null;
  area_util_m2?: number | null;
  tancagem_m3?: number | null;
  efluente_m3_dia?: number | null;
}

/** O que o sistema achou no texto da licença, sem IA. */
export interface LicencaIndiciosDe<I> {
  estruturas: I[];
  temas: I[];
  numeros: LicencaNumeros | null;
  corpo_receptor: string | null;
  secoes: string[];
  condicionantes: number | null;
  letras: number | null;
  gerado_em: string | null;
}
/** Na lista (sem trechos). */
export type LicencaIndicios = LicencaIndiciosDe<IndicioResumo>;
/** Em `pm_licenca_texto` (com trechos). */
export type LicencaIndiciosCompleto = LicencaIndiciosDe<IndicioCompleto>;

/** O que a IA escreveu ao ler a licença contra a diretriz (sob demanda). */
export interface LicencaLeituraTexto {
  /** 'alto' | 'medio' | 'baixo' */
  risco_diretriz: string;
  por_que: string[];
  estruturas: string[];
  o_que_o_orgao_vai_pedir: string[];
  condicionantes_relevantes: string[];
  gancho: string;
}

export interface LicencaLeitura {
  /** 'pronto' | 'erro' */
  status: string;
  modelo?: string | null;
  lido_em?: string | null;
  lido_por?: string | null;
  tokens_in?: number | null;
  tokens_out?: number | null;
  texto?: LicencaLeituraTexto | null;
  erro?: string | null;
  /** Com `status: 'pronto'`: a última tentativa de reler falhou e a leitura boa ficou. */
  ultimo_erro?: string | null;
  ultimo_erro_em?: string | null;
  ultimo_erro_por?: string | null;
}

export interface Prospecto {
  id: number;
  cnpj: string;
  cnpj_fmt: string | null;
  razao_social: string;
  municipio: string | null;
  cd_mun: number | null;
  endereco: string | null;
  codram: string | null;
  atividade: string | null;
  porte: string | null;
  potencial_poluidor: string | null;
  licenca: string | null;
  situacao_licenca: string | null;
  /** YYYY-MM-DD */
  fim_vigencia: string | null;
  /** YYYY-MM-DD */
  renovacao_ate: string | null;
  renovacao_situacao: string | null;
  processo_renovacao: string | null;
  lat: number | null;
  lon: number | null;
  situacao_mancha: SituacaoMancha | string;
  distancia_mancha_m: number | null;
  ficha_url: string | null;
  pdf_url: string | null;
  cliente_id: number | null;
  cliente_nome: string | null;
  status: StatusProspecto | string;
  status_em: string | null;
  status_por: string | null;
  observacoes: string | null;
  /** Volta junto no salvar, para o servidor detectar edição de outra pessoa (409). */
  atualizado_em: string;
  prioridade: Prioridade;
  /** null = o sistema ainda não leu o texto da licença. */
  licenca_indicios: LicencaIndicios | null;
  /** null = ninguém pediu a leitura com IA. */
  licenca_leitura: LicencaLeitura | null;
}

export interface Camada {
  id: number;
  codigo: string;
  nome: string;
  fonte: string | null;
  descricao: string | null;
  url_fonte: string | null;
  versao: string | null;
  cor: string;
  opacidade: number;
  ordem: number;
  /** [minx, miny, maxx, maxy] */
  bbox: Bbox | null;
  n_partes: number;
  /** Camada nova nasce desligada na tela; ausente = ligada. */
  ligada_padrao?: boolean;
}

export interface CampanhaResumo { codigo: string; nome?: string | null; total: number; importado_em: string | null }

/** rotulos[campo][codigo] = frase em português. */
export type Rotulos = Record<string, Record<string, string>>;

export interface Obter {
  pode: { editar: boolean };
  campanha: string | null;
  campanhas: CampanhaResumo[];
  camadas: Camada[];
  /** Uma PÁGINA do servidor; `juntarPaginas` monta a lista inteira. */
  prospectos: Prospecto[];
  resumo: {
    total: number;
    por_situacao: Record<string, number>;
    por_status: Record<string, number>;
    por_nivel: Record<string, number>;
  };
  rotulos: Rotulos;
  /** Total da campanha (todas as páginas). */
  total: number;
  offset: number;
  pagina: number;
  /** Quantos ainda faltam depois desta página; 0 = acabou. */
  restantes: number;
}

/** Nome da campanha para a tela: o que o servidor mandou, senão o código. */
export const nomeDaCampanha = (c: Pick<CampanhaResumo, 'codigo' | 'nome'>): string => (c.nome && c.nome.trim()) || c.codigo;

/**
 * Junta a primeira página com as seguintes: os prospectos somam (sem repetir id), o resto vem da primeira,
 * `restantes` é o da última página recebida.
 */
export function juntarPaginas(primeira: Obter, seguintes: Obter[]): Obter {
  const vistos = new Set<number>();
  const prospectos: Prospecto[] = [];
  for (const pagina of [primeira, ...seguintes]) {
    for (const p of pagina.prospectos) {
      if (vistos.has(p.id)) continue;
      vistos.add(p.id);
      prospectos.push(p);
    }
  }
  const ultima = seguintes.length ? seguintes[seguintes.length - 1] : primeira;
  return { ...primeira, prospectos, offset: 0, restantes: ultima.restantes };
}

export interface ParteCamada {
  chave: string;
  nome: string;
  bbox: Bbox;
  geojson: { type: 'Feature'; properties: Record<string, unknown> | null; geometry: unknown };
}
export interface CamadaPartes { codigo: string; partes: ParteCamada[]; restantes: number }

/** Listas vazias = "todos". */
export interface Filtros {
  busca: string;
  municipios: string[];
  situacoes: string[];
  niveis: string[];
  potenciais: string[];
  portes: string[];
  renovacoes: string[];
  status: string[];
}

// ── Constantes ───────────────────────────────────────────────────────────────
/** Abaixo deste zoom a tela não pede as manchas (são polígonos pesados). */
export const ZOOM_MINIMO_MANCHAS = 8;
export const LIMITE_OBSERVACOES = 2000;
/** Ordem de exibição nos Selects (o servidor manda só o dicionário). */
export const ORDEM_SITUACAO: string[] = ['dentro', 'ate_200m', 'ate_1km', 'mais_1km', 'sem_local_fixo', 'sem_coordenada'];
export const ORDEM_NIVEL: string[] = ['alta', 'media', 'baixa'];
export const ORDEM_STATUS: string[] = ['novo', 'contatado', 'reuniao', 'proposta', 'cliente', 'descartado'];
export const ORDEM_RENOVACAO: string[] = ['vencida_sem_renovacao', 'vencida_em_analise', 'prazo_passou', 'por_protocolar', 'protocolada'];
export const ORDEM_POTENCIAL: string[] = ['Alto', 'Médio', 'Baixo'];
export const ORDEM_PORTE: string[] = ['Excepcional', 'Grande', 'Médio', 'Pequeno', 'Minimo'];

const COR_SITUACAO: Record<string, string> = {
  dentro: '#B91626',
  ate_200m: '#D97706',
  ate_1km: '#CA8A04',
  mais_1km: '#6B7280',
};
const COR_SITUACAO_OUTRAS = '#9CA3AF';
const RAIO_NIVEL: Record<string, number> = { alta: 8, media: 6, baixa: 4 };

// ── Filtros ──────────────────────────────────────────────────────────────────
/** Padrão ao abrir: o que está dentro da mancha ou a até 200 m; tudo menos descartado. */
export function filtrosPadrao(): Filtros {
  return {
    busca: '',
    municipios: [],
    situacoes: ['dentro', 'ate_200m'],
    niveis: [],
    potenciais: [],
    portes: [],
    renovacoes: [],
    status: ORDEM_STATUS.filter((s) => s !== 'descartado'),
  };
}

export function semAcento(t: string | null | undefined): string {
  return String(t ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

const soDigitos = (t: string | null | undefined): string => String(t ?? '').replace(/\D/g, '');

const noGrupo = (lista: string[], valor: string | null | undefined): boolean =>
  lista.length === 0 || (valor != null && lista.includes(valor));

/**
 * Aplica os filtros. Cada lista vazia não restringe. A busca exige TODAS as palavras
 * (nome, atividade, CODRAM, município); texto só de números e máscara casa o CNPJ, com ou sem pontuação.
 */
export function aplicarFiltros(prospectos: Prospecto[], f: Filtros): Prospecto[] {
  const palavras = semAcento(f.busca).split(/\s+/).filter(Boolean);
  const busca = f.busca.trim();
  const ehCnpj = busca !== '' && /^[\d.\-/\s]+$/.test(busca);
  const digitos = ehCnpj ? soDigitos(busca) : '';
  return prospectos.filter((p) => {
    if (!noGrupo(f.municipios, p.municipio)) return false;
    if (!noGrupo(f.situacoes, p.situacao_mancha)) return false;
    if (!noGrupo(f.niveis, p.prioridade?.nivel)) return false;
    if (!noGrupo(f.potenciais, p.potencial_poluidor)) return false;
    if (!noGrupo(f.portes, p.porte)) return false;
    if (!noGrupo(f.renovacoes, p.renovacao_situacao)) return false;
    if (!noGrupo(f.status, p.status)) return false;
    if (palavras.length) {
      if (ehCnpj && digitos) return soDigitos(p.cnpj).includes(digitos);
      const alvo = semAcento(`${p.razao_social} ${p.atividade ?? ''} ${p.codram ?? ''} ${p.municipio ?? ''}`);
      if (!palavras.every((w) => alvo.includes(w))) return false;
    }
    return true;
  });
}

/** Alta primeiro, depois mais pontos, depois nome. Não altera a lista de entrada. */
export function ordenarPorPrioridade(prospectos: Prospecto[]): Prospecto[] {
  const peso = (p: Prospecto) => {
    const i = ORDEM_NIVEL.indexOf(p.prioridade?.nivel);
    return i === -1 ? 9 : i;
  };
  return [...prospectos].sort((a, b) =>
    peso(a) - peso(b)
    || (b.prioridade?.pontos ?? 0) - (a.prioridade?.pontos ?? 0)
    || a.razao_social.localeCompare(b.razao_social, 'pt-BR'));
}

export interface ResumoLista { total: number; dentro: number; ate_200m: number; alta: number }

export function resumoDaLista(prospectos: Prospecto[]): ResumoLista {
  const r: ResumoLista = { total: prospectos.length, dentro: 0, ate_200m: 0, alta: 0 };
  for (const p of prospectos) {
    if (p.situacao_mancha === 'dentro') r.dentro += 1;
    else if (p.situacao_mancha === 'ate_200m') r.ate_200m += 1;
    if (p.prioridade?.nivel === 'alta') r.alta += 1;
  }
  return r;
}

type CampoTexto = 'municipio' | 'potencial_poluidor' | 'porte' | 'situacao_licenca' | 'renovacao_situacao' | 'status';

/** Valores distintos (sem vazio) para os Selects; `ordem` lista primeiro os valores conhecidos, o resto vai em ordem alfabética. */
export function opcoesDe(prospectos: Prospecto[], campo: CampoTexto, ordem: string[] = []): string[] {
  const vistos = new Set<string>();
  for (const p of prospectos) {
    const v = p[campo];
    if (typeof v === 'string' && v.trim() !== '') vistos.add(v);
  }
  const conhecidos = ordem.filter((o) => vistos.has(o));
  const resto = Array.from(vistos).filter((v) => !ordem.includes(v)).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  return [...conhecidos, ...resto];
}

// ── Mapa ─────────────────────────────────────────────────────────────────────
export function corDaSituacao(situacao: string | null | undefined): string {
  return (situacao && COR_SITUACAO[situacao]) || COR_SITUACAO_OUTRAS;
}

export function raioDoNivel(nivel: string | null | undefined): number {
  return (nivel && RAIO_NIVEL[nivel]) || RAIO_NIVEL.baixa;
}

const temPonto = (p: Prospecto): boolean =>
  typeof p.lat === 'number' && typeof p.lon === 'number' && Number.isFinite(p.lat) && Number.isFinite(p.lon);

/** Só os que têm lat e lon. */
export function comCoordenada(prospectos: Prospecto[]): Prospecto[] {
  return prospectos.filter(temPonto);
}

/** Retângulo [minx, miny, maxx, maxy] dos pontos com coordenada; null se nenhum tem. */
export function bboxDaLista(prospectos: Prospecto[]): Bbox | null {
  let b: Bbox | null = null;
  for (const p of prospectos) {
    if (!temPonto(p)) continue;
    const x = p.lon as number;
    const y = p.lat as number;
    b = b ? [Math.min(b[0], x), Math.min(b[1], y), Math.max(b[2], x), Math.max(b[3], y)] : [x, y, x, y];
  }
  return b;
}

/** Dois retângulos se cruzam (encostar na borda conta). */
export function bboxCruza(a: Bbox, b: Bbox): boolean {
  return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
}

// ── Formatação ───────────────────────────────────────────────────────────────
/** 14 dígitos viram 00.000.000/0000-00; entrada já formatada fica igual; inválida volta como veio; vazia vira "—". */
export function fmtCnpj(v: string | null | undefined): string {
  const bruto = String(v ?? '').trim();
  if (!bruto) return '—';
  const d = soDigitos(bruto);
  if (d.length !== 14) return bruto;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/** "2026-10-16" (ou ISO com hora) vira "16/10/2026", por componentes, sem deslocar o dia. Texto inesperado volta como veio. */
export function fmtData(v: string | null | undefined): string {
  if (!v) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : v;
}

/** Dicionário do servidor; se o código não estiver lá, mostra o próprio código. */
export function rotulo(rotulos: Rotulos | null | undefined, campo: string, codigo: string | null | undefined): string {
  if (codigo == null || codigo === '') return '—';
  return rotulos?.[campo]?.[codigo] ?? codigo;
}

/** Data de hoje (local) como YYYY-MM-DD. */
export function hojeIso(d: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Meses inteiros de `hoje` até `fim` (YYYY-MM-DD). Negativo se já passou. null se alguma data for inválida. */
export function mesesAte(fim: string | null | undefined, hoje: string): number | null {
  const a = /^(\d{4})-(\d{2})-(\d{2})/.exec(hoje || '');
  const b = /^(\d{4})-(\d{2})-(\d{2})/.exec(fim || '');
  if (!a || !b) return null;
  let m = (Number(b[1]) - Number(a[1])) * 12 + (Number(b[2]) - Number(a[2]));
  const dh = Number(a[3]);
  const df = Number(b[3]);
  if (m > 0 && df < dh) m -= 1;
  else if (m < 0 && df > dh) m += 1;
  return m === 0 ? 0 : m;
}

/** "vence em 8 meses", "venceu há 5 meses", "vence em menos de 1 mês". */
export function textoVence(fim: string | null | undefined, hoje: string): string {
  const m = mesesAte(fim, hoje);
  if (m === null) return '—';
  const un = (n: number) => `${n} ${n === 1 ? 'mês' : 'meses'}`;
  if (m > 0) return `vence em ${un(m)}`;
  if (m < 0) return `venceu há ${un(-m)}`;
  return (fim as string).slice(0, 10) < hoje ? 'venceu há menos de 1 mês' : 'vence em menos de 1 mês';
}

/** Distância ao polígono mais próximo, em frase curta. 0 = dentro. */
export function textoDistancia(m: number | null | undefined): string {
  if (m == null) return '—';
  if (m <= 0) return 'dentro da mancha';
  if (m < 1000) return `${Math.round(m)} m da mancha`;
  return `${(m / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} km da mancha`;
}

// ── Camadas ligadas ao abrir ─────────────────────────────────────────────────
/** código → ligada ao abrir. `ligada_padrao` ausente (servidor antigo) = ligada. */
export function ligadasPadrao(camadas: Array<Pick<Camada, 'codigo' | 'ligada_padrao'>> | null | undefined): Record<string, boolean> {
  const r: Record<string, boolean> = {};
  for (const c of camadas ?? []) r[c.codigo] = c.ligada_padrao !== false;
  return r;
}

// ── O que a licença diz ──────────────────────────────────────────────────────
/** Cor de Tag do antd para o risco pela diretriz. Risco desconhecido ou vazio = cinza. */
export function corDoRisco(risco: string | null | undefined): 'red' | 'orange' | 'green' | 'default' {
  switch (semAcento(risco).trim()) {
    case 'alto': return 'red';
    case 'medio': return 'orange';
    case 'baixo': return 'green';
    default: return 'default';
  }
}

/** "Alto", "Médio", "Baixo"; o que não for conhecido volta como veio ("—" se vazio). */
export function rotuloDoRisco(risco: string | null | undefined): string {
  switch (semAcento(risco).trim()) {
    case 'alto': return 'Alto';
    case 'medio': return 'Médio';
    case 'baixo': return 'Baixo';
    default: return risco && risco.trim() ? risco.trim() : '—';
  }
}

/** Nomes das estruturas e depois dos temas achados na licença (sem repetir), para os chips. Sem indícios = lista vazia. */
export function resumoDosIndicios(
  ind: { estruturas?: Array<{ nome?: string | null }> | null; temas?: Array<{ nome?: string | null }> | null } | null | undefined,
): string[] {
  const nomes: string[] = [];
  for (const it of [...(ind?.estruturas ?? []), ...(ind?.temas ?? [])]) {
    const n = (it?.nome ?? '').trim();
    if (n && !nomes.includes(n)) nomes.push(n);
  }
  return nomes;
}

/** 47313.41 vira "47.313,41" (até 2 casas). Não número vira "—". */
export function fmtNumero(n: number | null | undefined, casas = 2): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { maximumFractionDigits: casas });
}

const positivo = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0;

/** Só os números que existem, nesta ordem: empregados, área útil, tancagem, efluente. Cada um já em frase curta. */
export function frasesDosNumeros(ind: { numeros?: LicencaNumeros | null } | null | undefined): string[] {
  const n = ind?.numeros;
  const f: string[] = [];
  if (!n) return f;
  if (positivo(n.empregados)) f.push(`${fmtNumero(n.empregados, 0)} ${n.empregados === 1 ? 'empregado' : 'empregados'}`);
  if (positivo(n.area_util_m2)) f.push(`Área útil: ${fmtNumero(n.area_util_m2)} m²`);
  if (positivo(n.tancagem_m3)) f.push(`Tancagem: ${fmtNumero(n.tancagem_m3)} m³`);
  if (positivo(n.efluente_m3_dia)) f.push(`Efluente: ${fmtNumero(n.efluente_m3_dia)} m³/dia`);
  return f;
}

/** "Corpo receptor: RIO DE SANTA CRUZ"; null se não existe. */
export function linhaCorpoReceptor(ind: { corpo_receptor?: string | null } | null | undefined): string | null {
  const c = (ind?.corpo_receptor ?? '').trim();
  return c ? `Corpo receptor: ${c}` : null;
}

/** "29 condicionantes em 7 seções"; null se o número não existe (ou é zero). Sem seções, só a contagem. */
export function linhaCondicionantes(ind: { condicionantes?: number | null; secoes?: string[] | null } | null | undefined): string | null {
  const n = ind?.condicionantes;
  if (!positivo(n)) return null;
  const s = (ind?.secoes ?? []).length;
  const base = `${fmtNumero(n, 0)} ${n === 1 ? 'condicionante' : 'condicionantes'}`;
  return s > 0 ? `${base} em ${s} ${s === 1 ? 'seção' : 'seções'}` : base;
}

/** Até `max` trechos não vazios de um item. */
export function trechosDe(item: { trechos?: string[] | null } | null | undefined, max = 3): string[] {
  return (item?.trechos ?? []).filter((t) => typeof t === 'string' && t.trim() !== '').slice(0, max);
}

/** Data (dia local) de um instante ISO com hora; sem hora ou inválido, cai no `fmtData`. */
export function fmtDataLocal(v: string | null | undefined): string {
  if (!v) return '—';
  if (!/T\d{2}:\d{2}/.test(v)) return fmtData(v);
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return fmtData(v);
  const p2 = (x: number) => String(x).padStart(2, '0');
  return `${p2(d.getDate())}/${p2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** "haiku:us.anthropic…" vira "Haiku"; modelo desconhecido volta como veio. */
export function nomeDoModelo(modelo: string | null | undefined): string {
  const m = (modelo ?? '').toLowerCase();
  if (m.includes('haiku')) return 'Haiku';
  if (m.includes('sonnet')) return 'Sonnet';
  if (m.includes('opus')) return 'Opus';
  return (modelo ?? '').trim();
}
